'use client';

import React, { useState, useEffect } from 'react';
import GalleryHero from './GalleryHero';
import CategoryFilter from './CategoryFilter';
import GalleryGrid from './GalleryGrid';
import Lightbox from './Lightbox';
import ComingSoonSection from './ComingSoonSection';
import GuestPhotoUpload from './GuestPhotoUpload';
import { createClient } from '@/lib/supabase/client';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  category: string;
  caption?: string;
  culturalContext?: string;
}

interface Category {
  id: string;
  label: string;
  count: number;
}

const GalleryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [guestPhotos, setGuestPhotos] = useState<GalleryImage[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    fetchGuestPhotos();
  }, []);

  const fetchGuestPhotos = async () => {
    try {
      const supabase = createClient();
      
      // Fetch approved photos
      const { data: photos, error } = await supabase
        .from('guest_photos')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform to GalleryImage format
      const photoList = Array.isArray(photos) ? photos : [];
      const transformedPhotos: GalleryImage[] = photoList.map((photo) => {
        // Get public URL for the photo
        const { data: urlData } = supabase.storage
          .from('guest-photos')
          .getPublicUrl(photo.storage_path);

        // Add cache-busting parameter to ensure fresh image loads
        const photoUrl = `${urlData.publicUrl}?t=${Date.now()}`;

        return {
          id: photo.id,
          src: photoUrl,
          alt: photo.caption || `Photo by ${photo.submitter_name}`,
          category: photo.category,
          caption: photo.caption || undefined,
          culturalContext: `Shared by ${photo.submitter_name}`,
        };
      });

      setGuestPhotos(transformedPhotos);
    } catch (error) {
      console.error('Error fetching guest photos:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const staticImages: GalleryImage[] = [
  {
    id: '1',
    src: "/assets/images/M2-1783667152267.jpg",
    alt: 'Smiling couple in traditional South Sudanese attire standing together outdoors with golden sunset background',
    category: 'engagement',
    caption: 'Our Engagement Celebration',
    culturalContext: 'Wearing traditional South Sudanese garments to honor our heritage during the engagement ceremony.'
  },
  {
    id: '2',
    src: "/assets/images/G2-1783667181200.jpg",
    alt: 'Bride in elegant white wedding dress holding colorful bouquet of roses and lilies',
    category: 'preparation',
    caption: 'Bridal Preparation',
    culturalContext: 'The bride prepares for the sacred covenant ceremony with prayer and family blessings.'
  },
  {
    id: '3',
    src: "/assets/images/M1-1783667127886.jpg",
    alt: 'Groom in dark navy suit with gold tie adjusting cufflinks in modern room',
    category: 'preparation',
    caption: 'Groom Getting Ready',
    culturalContext: 'Preparing with reverence and gratitude for the covenant commitment ahead.'
  },
  {
    id: '4',
    src: "/assets/images/M3-1783667195806.jpg",
    alt: 'Extended family members in colorful traditional African clothing gathered together smiling',
    category: 'family',
    caption: 'Family Gathering',
    culturalContext: 'Our families unite in celebration, representing the community blessing central to South Sudanese weddings.'
  },
  {
    id: '5',
    src: "/assets/images/G3-1783667211182.jpg",
    alt: 'Close-up of two hands exchanging gold wedding rings with soft lighting',
    category: 'engagement',
    caption: 'The Promise',
    culturalContext: 'Exchanging rings as a symbol of our eternal covenant before God and community.'
  },
  {
    id: '6',
    src: "/assets/images/M4-1783667265990.jpg",
    alt: 'Bride and groom holding hands walking through garden with white flowers',
    category: 'engagement',
    caption: 'Walking Together',
    culturalContext: 'Beginning our journey as one, blessed by divine timing and community support.'
  },
  {
    id: '7',
    src: "/assets/images/G4-1783667300746.jpg",
    alt: 'Group of bridesmaids in matching olive green dresses holding bouquets outdoors',
    category: 'family',
    caption: 'Bridesmaids',
    culturalContext: 'Sisters and friends who have supported our journey with prayer and encouragement.'
  },
  {
    id: '8',
    src: "/assets/images/M6-1783667347504.jpg",
    alt: 'Couple silhouette against orange and purple sunset sky with trees',
    category: 'engagement',
    caption: 'Divine Timing',
    culturalContext: 'Celebrating God\'s perfect timing in bringing us together at this beautiful moment.'
  },
  {
    id: '9',
    src: "/assets/images/G5-1783667358491.jpg",
    alt: 'Wedding invitation cards with gold calligraphy on ivory paper with floral decorations',
    category: 'preparation',
    caption: 'Sacred Invitations',
    culturalContext: 'Invitations designed with traditional South Sudanese patterns and Christian symbolism.'
  },
  {
    id: '10',
    src: "/assets/images/M7-1783667399800.jpg",
    alt: 'Elderly couple in traditional attire smiling warmly at camera with wisdom in their eyes',
    category: 'family',
    caption: 'Elder Blessings',
    culturalContext: 'Receiving wisdom and blessings from family elders, a cornerstone of South Sudanese wedding traditions.'
  },
  {
    id: '11',
    src: "/assets/images/G1-1783667140644.jpg",
    alt: 'Wedding venue decorated with white drapes, gold accents, and olive green floral arrangements',
    category: 'preparation',
    caption: 'Venue Preparation',
    culturalContext: 'Creating a sacred space that honors both our faith and cultural heritage.'
  },
  {
    id: '12',
    src: "/assets/images/2024_01_27_19_56_IMG_1900-1783667460728.jpg",
    alt: 'Couple laughing together in casual attire sitting on wooden bench in park',
    category: 'engagement',
    caption: 'Joyful Moments',
    culturalContext: 'The joy that comes from a relationship built on faith, respect, and divine timing.'
  }];

  // Combine static images with guest photos
  const allImages = [...staticImages, ...guestPhotos];

  const categories: Category[] = [
  { id: 'all', label: 'All Photos', count: allImages.length },
  { id: 'engagement', label: 'Engagement', count: allImages.filter((img) => img.category === 'engagement').length },
  { id: 'family', label: 'Family', count: allImages.filter((img) => img.category === 'family').length },
  { id: 'preparation', label: 'Preparation', count: allImages.filter((img) => img.category === 'preparation').length },
  { id: 'ceremony', label: 'Ceremony', count: allImages.filter((img) => img.category === 'ceremony').length },
  { id: 'reception', label: 'Reception', count: allImages.filter((img) => img.category === 'reception').length }];


  const filteredImages = activeCategory === 'all' ?
  allImages :
  allImages.filter((img) => img.category === activeCategory);

  const handleImageClick = (image: GalleryImage) => {
    if (!isHydrated) return;
    setSelectedImage(image);
    const index = filteredImages.findIndex((img) => img.id === image.id);
    setCurrentImageIndex(index);
  };

  const handleNext = () => {
    if (!isHydrated) return;
    const nextIndex = (currentImageIndex + 1) % filteredImages.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(filteredImages[nextIndex]);
  };

  const handlePrevious = () => {
    if (!isHydrated) return;
    const prevIndex = currentImageIndex === 0 ? filteredImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(filteredImages[prevIndex]);
  };

  const handleClose = () => {
    if (!isHydrated) return;
    setSelectedImage(null);
  };

  const handleCategoryChange = (categoryId: string) => {
    if (!isHydrated) return;
    setActiveCategory(categoryId);
  };

  const handleUploadSuccess = () => {
    // Refresh photos after successful upload
    fetchGuestPhotos();
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse">
          <div className="bg-surface h-96"></div>
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="h-12 bg-surface rounded mb-8"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="aspect-[4/3] bg-surface rounded-lg"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <GalleryHero
        title="Our Journey"
        subtitle="A Visual Testament to God's Perfect Timing"
        description="Explore the moments that have shaped our love story, from engagement celebrations to family gatherings. Each image captures the divine timing and community blessings that have brought us to this sacred covenant." />


      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Guest Photo Upload Section */}
        <div className="mb-12 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 rounded-lg p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="font-playfair text-2xl font-semibold text-foreground mb-3">
              Share Your Wedding Moments
            </h3>
            <p className="font-inter text-base text-muted-foreground mb-6">
              Capture and share special moments from our ceremony and reception. Your photos will be reviewed and added to our gallery.
            </p>
            <GuestPhotoUpload onUploadSuccess={handleUploadSuccess} />
          </div>
        </div>

        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange} />

        {isLoadingPhotos ? (
          <div className="text-center py-12">
            <p className="font-inter text-base text-muted-foreground">Loading photos...</p>
          </div>
        ) : (
          <GalleryGrid
            images={filteredImages}
            onImageClick={handleImageClick} />
        )}

        <div className="mt-16 space-y-12">
          <ComingSoonSection
            title="More Ceremony Moments"
            description="Additional photographs from our wedding ceremony will be added here after the celebration. These images will capture the sacred covenant moment, family blessings, and the joy of our union before God and community."
            icon="SparklesIcon" />


          <ComingSoonSection
            title="More Reception Celebration"
            description="More reception photos showcasing the joyous celebration with family and friends will be available soon. These images will highlight South Sudanese cultural traditions, dancing, and the community feast that honors our heritage."
            icon="CakeIcon" />

        </div>
      </main>

      {selectedImage &&
      <Lightbox
        image={selectedImage}
        onClose={handleClose}
        onNext={handleNext}
        onPrevious={handlePrevious}
        currentIndex={currentImageIndex}
        totalImages={filteredImages.length} />

      }
    </div>);

};

export default GalleryInteractive;